import type * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { Textarea } from "~/atoms/textarea";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        // Base: layout, sizing, shape, transition
        "group/input-group relative flex h-8 w-full min-w-0 items-center rounded-lg border transition-colors outline-none",
        // Default colors
        "border-border bg-input/70",
        // Dark
        "dark:bg-input/30",
        // Focus-visible (via child)
        "has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 has-[[data-slot=input-group-control]:focus-visible]:ring-3",
        // Disabled (via child)
        "has-disabled:bg-input/50 has-disabled:opacity-50",
        // Dark + disabled
        "dark:has-disabled:bg-input/80",
        // Aria-invalid (via child)
        "has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:ring-3",
        // Dark + aria-invalid
        "dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40",
        // Inside combobox-content
        "in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0",
        // Block-align layout overrides
        "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>textarea]:h-auto",
        // Block-align input padding overrides
        "has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5",
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  [
    // Base: layout, sizing, typography, cursor
    "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium select-none",
    // Default colors
    "text-muted-foreground",
    // Group disabled
    "group-data-[disabled=true]/input-group:opacity-50",
    // Child overrides
    "[&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      align: {
        "inline-start":
          "pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem] order-first",
        "inline-end":
          "pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem] order-last",
        "block-start":
          "px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start",
        "block-end":
          "px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva(
  // Base: layout, typography
  "flex items-center gap-2 text-sm shadow-none",
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-[calc(var(--radius)-3px)] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "",
        "icon-xs":
          "size-6 rounded-[calc(var(--radius)-3px)] p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0",
      },
    },
    defaultVariants: {
      size: "xs",
    },
  },
);

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        // Base: layout, typography
        "flex items-center gap-2 text-sm",
        // Default colors
        "text-muted-foreground",
        // SVG overrides
        "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<"input">) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        // Base: layout, shape, reset border/ring/shadow
        "flex-1 rounded-none border-0 shadow-none ring-0",
        // Default colors (transparent — group handles bg)
        "bg-transparent",
        // Dark
        "dark:bg-transparent",
        // Focus-visible (reset — group handles ring)
        "focus-visible:ring-0",
        // Disabled
        "disabled:bg-transparent",
        // Dark + disabled
        "dark:disabled:bg-transparent",
        // Aria-invalid (reset — group handles ring)
        "aria-invalid:ring-0",
        className,
      )}
      {...props}
    />
  );
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        // Base: layout, shape, reset border/ring/shadow
        "flex-1 resize-none rounded-none border-0 py-2 shadow-none ring-0",
        // Default colors (transparent — group handles bg)
        "bg-transparent",
        // Dark
        "dark:bg-transparent",
        // Focus-visible (reset — group handles ring)
        "focus-visible:ring-0",
        // Disabled
        "disabled:bg-transparent",
        // Dark + disabled
        "dark:disabled:bg-transparent",
        // Aria-invalid (reset — group handles ring)
        "aria-invalid:ring-0",
        className,
      )}
      {...props}
    />
  );
}

/** @knipignore */
export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
